import { useCallback, useMemo } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import { useMovementPageData } from 'src/app/pages/movement/pageContext';
import { useCurrentUser, useIsStaff, useLoginState } from 'src/app/shared/hooks/session';

import { removePetitionFromMovement } from './api';

type Result = ModelHookResult<
	{
		isLoading: boolean;
		isSuccess: boolean;
		errorMessage: string | null;
		userCanRemoveThePetition: boolean;
	},
	{
		removePetitionFromMovement: ({ close }: { close: () => void }) => Promise<void>;
		resetModal: () => void;
	}
>;

export function useRemoveOption({
	petitionId,
	petitionOwnerId,
}: {
	petitionId: string;
	petitionOwnerId: string;
}): Result {
	const utilityContext = useUtilityContext();
	const queryClient = useQueryClient();
	const isStaff = useIsStaff();
	const userIsAuthenticated = useLoginState() === 'AUTHENTICATED';

	const currentUser = useCurrentUser();

	const {
		data: {
			movement: { id: movementId },
		},
	} = useMovementPageData();

	const {
		mutateAsync: removePetition,
		isPending: isLoading,
		isSuccess,
		isError,
		error,
		reset,
	} = useMutation({
		mutationFn: async () =>
			removePetitionFromMovement({
				petitionId,
				movementId,
				utilityContext,
			}),
	});

	const userCanRemoveThePetition = useMemo(() => {
		const isPetitionOwner = currentUser?.id ? currentUser.id === petitionOwnerId : false;
		return userIsAuthenticated && (isStaff || isPetitionOwner);
	}, [isStaff, currentUser?.id, petitionOwnerId, userIsAuthenticated]);

	return {
		data: {
			isLoading,
			isSuccess,
			errorMessage: isError && error instanceof Error ? error.message : null,
			userCanRemoveThePetition,
		},
		actions: {
			removePetitionFromMovement: useCallback(
				async ({ close }) => {
					try {
						if (!userCanRemoveThePetition) return;
						await removePetition();
						await queryClient.invalidateQueries({
							queryKey: ['petitions-in-movement', movementId],
						});
						setTimeout(() => {
							close();
						}, 3000);
					} catch (e) {
						// Error handled by isError and errorMessage
						// No additional action needed here
					}
				},
				[removePetition, queryClient, movementId, userCanRemoveThePetition],
			),
			resetModal: useCallback(() => {
				reset();
			}, [reset]),
		},
	};
}
